import { Button, Spin, Tag } from "antd";
import { Briefcase, Pencil, Shield, Store } from "lucide-react";

import type { OPSUserRoleVO } from "@/api/accounts/type";
import { AuthButton } from "@/components/AuthButton";
import { AppPermissionSection } from "@/components/PermissionTree";
import { APP_INFO } from "@/config/system";
import { usePermission } from "@/hooks/usePermission";
import { cn } from "@/lib/cn";

import { usePermissionDetailPanel } from "../../../PermissionDetail/hooks/usePermissionDetailPanel";

/** 右侧权限详情面板 props */
interface PermissionDetailPanelProps {
  /** 当前选中的实体角色 */
  selectedEntityRole: OPSUserRoleVO | null;
  /** 当前账号用户 ID，用于绑定状态切换 */
  userId?: string;
  /** 身份状态切换成功后刷新回调 */
  onStatusChanged?: () => void | Promise<void>;
}

/** 右侧权限详情：角色摘要 + 权限树（支持编辑保存） */
export const PermissionDetailPanel = ({
  selectedEntityRole,
  userId,
  onStatusChanged,
}: PermissionDetailPanelProps) => {
  const {
    roles,
    rolesLoading,
    isEditing,
    saveLoading,
    statusLoading,
    permissionValue,
    startEditing,
    cancelEditing,
    saveChanges,
    updateDraftSelection,
    onToggleStatus,
  } = usePermissionDetailPanel(selectedEntityRole, userId, onStatusChanged);

  const canChangeRoleStatus = usePermission("ops:account:changeRoleStatus");

  if (!selectedEntityRole) {
    return (
      <div className="flex flex-1 items-center justify-center text-slate-400 h-full">
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <p className="text-lg m-0">请从左侧选择一个身份</p>
          <p className="text-sm mt-1">查看该角色拥有的权限</p>
        </div>
      </div>
    );
  }

  const appInfo = APP_INFO[selectedEntityRole.systemTypeCode as keyof typeof APP_INFO];
  const isEnabled = selectedEntityRole.status === "ENABLED";
  // 停用身份不允许编辑权限
  const canEdit = isEnabled && roles.length > 0;

  return (
    <div className="flex flex-col gap-3 px-3 py-4 mt-2 pt-0 min-h-0">
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 shrink-0">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center shrink-0">
            <Briefcase className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="text-base font-semibold m-0">{selectedEntityRole.roleName}</h3>
              {appInfo && (
                <Tag bordered className="text-xs m-0">
                  {appInfo.abbr} · {appInfo.name}
                </Tag>
              )}
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
              <span className="inline-flex items-center gap-1">
                <Store className="w-3 h-3" />
                {selectedEntityRole.orgName || "—"}
              </span>
            </div>
          </div>
          <Tag
            bordered={false}
            className={cn(
              "text-xs m-0 shrink-0 cursor-pointer select-none",
              isEnabled ? "bg-green-100! text-green-700!" : "bg-red-100! text-red-700!",
              (statusLoading || isEditing) && "opacity-60 pointer-events-none",
            )}
            onClick={() => canChangeRoleStatus && void onToggleStatus()}
          >
            {statusLoading ? "切换中..." : isEnabled ? "已启用" : "已停用"}
          </Tag>
        </div>
      </div>
      <div className="min-h-0 space-y-4">
        {rolesLoading ? (
          <div className="flex justify-center py-8">
            <Spin tip="加载权限中..." />
          </div>
        ) : (
          <AppPermissionSection
            params={{
              orgType: selectedEntityRole.orgType,
            }}
            readOnly={!isEditing}
            value={permissionValue}
            onChange={isEditing ? updateDraftSelection : undefined}
          />
        )}

        <AuthButton code="ops:account:editRole">
          {roles.length > 0 && (
            <div>
              {!isEditing ? (
                <Button
                  icon={<Pencil className="w-4 h-4" />}
                  disabled={!canEdit}
                  onClick={startEditing}
                >
                  编辑权限
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Button disabled={saveLoading} onClick={cancelEditing}>
                    取消
                  </Button>
                  <Button type="primary" loading={saveLoading} onClick={() => void saveChanges()}>
                    保存修改
                  </Button>
                </div>
              )}
            </div>
          )}
        </AuthButton>
      </div>
    </div>
  );
};
